"use client";

import * as React from "react";
import {
  Maximize2,
  Minimize2,
  Plus,
  X,
  Search as SearchIcon,
  ArrowLeft,
} from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { CardHeader } from "@/components/ui/card";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePersonation } from "@/lib/personation-context";
import { Search } from "@/components/ui/search/search";
import { cn } from "@/lib/utils";

import { useWidgetMode } from "./widget-mode-context";
import { type DragHandleProps } from "./widget-overlay-context";

function formatSourcesTitle(template: string, count: number): string {
  return template.replace("{count}", String(count));
}

export type WidgetHeaderProps = {
  isFullScreen?: boolean;
  isMobile?: boolean;
  accentColor?: string;
  onFullScreenToggle?: () => void;
  onNewRoom?: () => void;
  onClose?: () => void;
  isDragging?: boolean;
  dragHandleProps?: DragHandleProps;
};

export function WidgetHeader({
  isFullScreen = false,
  isMobile = false,
  accentColor,
  onFullScreenToggle,
  onNewRoom,
  onClose,
  isDragging = false,
  dragHandleProps,
}: WidgetHeaderProps) {
  const { mode, setMode, sourcesData, closeSources } = useWidgetMode();
  const { botName, botRole, botAvatarUrl, labels } = usePersonation();

  // Sources mode header - shows back button + "Sources (N)" + expand/close
  if (mode === "sources" && sourcesData) {
    return (
      <CardHeader
        ref={dragHandleProps?.ref}
        className={cn(
          "flex flex-row items-center gap-2 px-4 py-3",
          !isMobile && (isDragging ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing")
        )}
        {...(!isMobile ? dragHandleProps?.listeners : {})}
      >
        {/* Back button */}
        <Button
          size="icon"
          variant="ghost"
          onClick={closeSources}
          aria-label="Back"
          data-testid="sources-back-button"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>

        {/* Title */}
        <span className="flex-1 font-medium text-sm truncate">
          {formatSourcesTitle(labels.sources.sourcesTitle, sourcesData.sources.length)}
        </span>

        {/* Fullscreen Toggle - only on desktop */}
        {!isMobile && (
          <Button
            size="icon"
            variant="ghost"
            data-testid="widget-fullscreen-toggle"
            aria-label={isFullScreen ? "Minimize" : "Maximize"}
            onClick={onFullScreenToggle}
          >
            {isFullScreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        )}

        {/* Close Button */}
        {onClose && (
          <Button
            size="icon"
            variant="ghost"
            data-testid="widget-close-button"
            aria-label="Close"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
    );
  }

  // Chat mode header - shows avatar, name, lupa icon
  if (mode === "chat") {
    // Mobile chat header: Avatar + Name + Search/NewChat/Close buttons
    if (isMobile) {
      return (
        <CardHeader
          ref={dragHandleProps?.ref}
          className="flex flex-row items-center pt-5"
        >
          <div className="flex items-center space-x-4">
            <Avatar>
              <AvatarImage src={botAvatarUrl} alt="Bot Avatar" />
              <AvatarFallback>{botName?.[0] ?? "B"}</AvatarFallback>
            </Avatar>
            <p className="text-sm font-medium leading-none select-none">{botName}</p>
          </div>
          <div className="flex ml-auto gap-2">
            {/* New Chat Button */}
            {onNewRoom && (
              <Button
                size="icon"
                variant="outline"
                className="rounded-full"
                data-testid="widget-new-chat-button"
                onClick={onNewRoom}
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}

            {/* Search Button */}
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setMode("search")}
              aria-label="Search"
              data-testid="mode-toggle-search"
            >
              <SearchIcon className="h-4 w-4" />
            </Button>

            {/* Close Button */}
            {onClose && (
              <Button
                size="icon"
                variant="ghost"
                data-testid="widget-close-button"
                aria-label="Close"
                onClick={onClose}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardHeader>
      );
    }

    // Desktop chat header
    return (
      <CardHeader
        ref={dragHandleProps?.ref}
        className={cn(
          "flex flex-row items-center pt-5",
          isDragging ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing"
        )}
        {...dragHandleProps?.listeners}
      >
        <div className="flex items-center space-x-4">
          <Avatar>
            <AvatarImage src={botAvatarUrl} alt="Bot Avatar" />
            <AvatarFallback>{botName?.[0] ?? "B"}</AvatarFallback>
          </Avatar>
          <div className="select-none">
            <p className="text-sm font-medium leading-none">{botName}</p>
            <p className="text-sm text-muted-foreground">{botRole}</p>
          </div>
        </div>
        <div className="flex ml-auto gap-2">
          {/* Search mode button (lupa) */}
          <Button
            size="icon"
            variant="ghost"
            onClick={() => setMode("search")}
            aria-label="Search mode"
            data-testid="mode-toggle-search"
          >
            <SearchIcon className="h-4 w-4" />
          </Button>

          {/* Fullscreen Toggle */}
          <Button
            size="icon"
            variant="ghost"
            data-testid="widget-fullscreen-toggle"
            aria-label={isFullScreen ? "Minimize" : "Maximize"}
            onClick={onFullScreenToggle}
          >
            {isFullScreen ? <Minimize2 /> : <Maximize2 />}
          </Button>

          {/* New Chat Button */}
          {onNewRoom && (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="icon"
                    variant="outline"
                    className="rounded-full"
                    data-testid="widget-new-chat-button"
                    onClick={onNewRoom}
                  >
                    <Plus />
                  </Button>
                </TooltipTrigger>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Close Button */}
          {onClose && (
            <Button
              size="icon"
              variant="ghost"
              data-testid="widget-close-button"
              aria-label="Close"
              onClick={onClose}
            >
              <X />
            </Button>
          )}
        </div>
      </CardHeader>
    );
  }

  // Search mode header - shows back button + search input + chat icon
  return (
    <CardHeader
      ref={dragHandleProps?.ref}
      className={cn(
        "flex flex-row items-center gap-2 px-4 py-3",
        !isMobile && (isDragging ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing")
      )}
      {...(!isMobile ? dragHandleProps?.listeners : {})}
    >
      {/* Back button */}
      <Button
        size="icon"
        variant="ghost"
        onClick={() => setMode("chat")}
        aria-label="Back"
        data-testid="search-back-button"
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>

      {/* Search input */}
      <Search.Input />

      {/* Fullscreen Toggle - only on desktop */}
      {!isMobile && (
        <Button
          size="icon"
          variant="ghost"
          data-testid="widget-fullscreen-toggle"
          aria-label={isFullScreen ? "Minimize" : "Maximize"}
          onClick={onFullScreenToggle}
        >
          {isFullScreen ? <Minimize2 /> : <Maximize2 />}
        </Button>
      )}

      {/* Close Button */}
      {onClose && (
        <Button
          size="icon"
          variant="ghost"
          data-testid="widget-close-button"
          aria-label="Close"
          onClick={onClose}
        >
          <X />
        </Button>
      )}
    </CardHeader>
  );
}

WidgetHeader.displayName = "Widget.Header";
